import { useMutation, useQuery } from "@tanstack/react-query";
import { Link, useSearch } from "@tanstack/react-router";
import type { Money, Registrant } from "@websede/contracts";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../../api/client.js";
import { useSession } from "../../auth/session.js";
import { Button, Card, ErrorNotice, Field, Input, Select } from "../../components/form.js";

function formatMoney(money: Money, locale: string): string {
  return new Intl.NumberFormat(locale, { style: "currency", currency: money.currency }).format(
    money.amountMinor / 100,
  );
}

/** Registrant details, a term and the three-line price; payment happens on the hosted page. */
export function CheckoutPage() {
  const { t, i18n } = useTranslation();
  const search = useSearch({ from: "/admin/domains/checkout" });
  const session = useSession();
  const [years, setYears] = useState(search.years);
  const [whoisPrivacy, setWhoisPrivacy] = useState(true);
  const me = session.data?.user;
  const [nameFirst, ...nameRest] = (me?.displayName ?? "").split(" ");
  const [registrant, setRegistrant] = useState<Registrant>({
    firstName: nameFirst ?? "",
    lastName: nameRest.join(" "),
    email: me?.email ?? "",
    phone: "",
    street: "",
    city: "",
    postalCode: "",
    countryCode: "",
  });
  const update = (key: keyof Registrant) => (value: string) =>
    setRegistrant((r) => ({ ...r, [key]: value }));

  const quote = useQuery({
    queryKey: ["orders", "quote", search.domain, years],
    queryFn: () => api.orders.quote({ items: [{ domain: search.domain, years }] }),
    enabled: search.domain.length > 0,
  });
  const item = quote.data?.items[0];

  const create = useMutation({
    mutationFn: () =>
      api.orders.create({
        items: [{ domain: search.domain, years }],
        registrant: {
          ...registrant,
          ...(registrant.organization ? {} : { organization: undefined }),
          ...(registrant.stateOrProvince ? {} : { stateOrProvince: undefined }),
        },
        whoisPrivacy,
      }),
    onSuccess: (result) => {
      window.location.assign(result.checkoutUrl);
    },
  });

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    create.mutate();
  };

  if (!search.domain) {
    return (
      <section className="p-6">
        <Link to="/admin/domains" className="text-accent underline-offset-2 hover:underline">
          {t("admin.nav.domains")}
        </Link>
      </section>
    );
  }

  return (
    <section className="mx-auto flex w-full max-w-4xl flex-col gap-6 p-4 md:p-8">
      <div>
        <h1 className="text-[22px] text-ink">{t("checkout.title")}</h1>
        <p className="font-mono text-sm text-ink-2">{search.domain}</p>
      </div>
      <form onSubmit={onSubmit} className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="flex flex-col gap-6">
          <Card title={t("checkout.registrant")}>
            <p className="text-sm text-ink-2">{t("checkout.registrantHint")}</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={t("checkout.firstName")}>
                <Input
                  required
                  value={registrant.firstName}
                  onChange={(e) => update("firstName")(e.target.value)}
                />
              </Field>
              <Field label={t("checkout.lastName")}>
                <Input
                  required
                  value={registrant.lastName}
                  onChange={(e) => update("lastName")(e.target.value)}
                />
              </Field>
              <Field label={t("checkout.organization")}>
                <Input
                  value={registrant.organization ?? ""}
                  onChange={(e) => update("organization")(e.target.value)}
                />
              </Field>
              <Field label={t("checkout.email")}>
                <Input
                  type="email"
                  required
                  value={registrant.email}
                  onChange={(e) => update("email")(e.target.value)}
                />
              </Field>
              <Field label={t("checkout.phone")}>
                <Input
                  required
                  placeholder="+46701234567"
                  value={registrant.phone}
                  onChange={(e) => update("phone")(e.target.value)}
                />
              </Field>
              <Field label={t("checkout.street")}>
                <Input
                  required
                  value={registrant.street}
                  onChange={(e) => update("street")(e.target.value)}
                />
              </Field>
              <Field label={t("checkout.city")}>
                <Input
                  required
                  value={registrant.city}
                  onChange={(e) => update("city")(e.target.value)}
                />
              </Field>
              <Field label={t("checkout.postalCode")}>
                <Input
                  required
                  value={registrant.postalCode}
                  onChange={(e) => update("postalCode")(e.target.value)}
                />
              </Field>
              <Field label={t("checkout.country")}>
                <Input
                  required
                  maxLength={2}
                  placeholder="SE"
                  className="uppercase"
                  value={registrant.countryCode}
                  onChange={(e) => update("countryCode")(e.target.value.toUpperCase())}
                />
              </Field>
              <Field label={t("checkout.state")}>
                <Input
                  value={registrant.stateOrProvince ?? ""}
                  onChange={(e) => update("stateOrProvince")(e.target.value)}
                />
              </Field>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={whoisPrivacy}
                onChange={(e) => setWhoisPrivacy(e.target.checked)}
              />
              {t("checkout.privacy")}
            </label>
          </Card>
        </div>

        <div className="flex flex-col gap-4">
          <Card title={t("checkout.summary")}>
            <Field label={t("checkout.term")}>
              <Select value={String(years)} onChange={(e) => setYears(Number(e.target.value))}>
                {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                  <option key={n} value={n}>
                    {t("checkout.years", { count: n })}
                  </option>
                ))}
              </Select>
            </Field>
            <ErrorNotice error={quote.error} />
            {item?.price ? (
              <dl className="flex flex-col gap-1 text-sm">
                <div className="flex justify-between">
                  <dt className="text-ink-2">{t("checkout.wholesale")}</dt>
                  <dd className="tabular-nums">
                    {formatMoney(item.price.wholesale, i18n.language)}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-ink-2">{t("checkout.icann")}</dt>
                  <dd className="tabular-nums">
                    {formatMoney(item.price.icannFee, i18n.language)}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-ink-2">{t("checkout.processing")}</dt>
                  <dd className="tabular-nums">
                    {formatMoney(item.price.processingFee, i18n.language)}
                  </dd>
                </div>
                <div className="mt-2 flex justify-between border-t border-rule pt-2 font-semibold">
                  <dt>{t("checkout.total")}</dt>
                  <dd className="tabular-nums">{formatMoney(item.price.total, i18n.language)}</dd>
                </div>
              </dl>
            ) : item ? (
              <p className="text-sm text-danger">
                {t("checkout.unavailable", {
                  reason: t(`domains.${item.reason ?? "unsupported"}`),
                })}
              </p>
            ) : (
              <p className="text-sm text-ink-3">{t("common.loading")}</p>
            )}
            <ErrorNotice error={create.error} />
            <Button type="submit" disabled={!item?.price || create.isPending}>
              {item?.price
                ? t("checkout.pay", { amount: formatMoney(item.price.total, i18n.language) })
                : t("common.loading")}
            </Button>
            <p className="text-xs text-ink-3">{t("checkout.hosted")}</p>
            <p className="text-xs text-ink-3">{t("checkout.note")}</p>
          </Card>
        </div>
      </form>
    </section>
  );
}
